import React, { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { getCookie } from 'cookies-next'
import { When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { AnalyticsIcon } from '@/atoms/Icons/AnalyticsIcon'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { EnvelopeIcon } from '@/atoms/Icons/EnvelopeIcon'
import { FacebookIcon } from '@/atoms/Icons/FacebookIcon'
import { LinkIcon } from '@/atoms/Icons/LinkIcon'
import { RedditIcon } from '@/atoms/Icons/RedditIcon'
import { TelegramIcon } from '@/atoms/Icons/TelegramIcon'
import { TwitterXIcon } from '@/atoms/Icons/TwitterXIcon'
import { ParagraphMD } from '@/atoms/Text'
import { OneColorIconProps } from '@/atoms/utils'
import { SEGMENT_USER_ID_COOKIE } from '@/constants/cookies'
import { paths } from '@/constants/paths'
import { NamedColor } from '@/constants/types'
import { useToast } from '@/molecules/Toast'
import { useUser } from '@/services/UserService'
import { useLoginUrl } from '@/utils/auth-utils'
import { useTranslate } from '@/utils/translate/translate-client'

interface SocialSharingProps {
  color?: NamedColor
  url?: string
  vertical?: boolean
  onShare?: (network: string) => void
  viewImpact?: () => void
  showImpact?: boolean
  projectSlug: string
}

interface SocialShare {
  name: string
  icon: React.FC<OneColorIconProps>
  href?: string
  onClick?: () => void
  brandColor: string
}

interface UseSocialSharingValues {
  links: SocialShare[]
  socials: Record<string, SocialShare>
}

export const createWebSharingUrl = (urlString: string): string => {
  const userId = getCookie(SEGMENT_USER_ID_COOKIE)
  const url = new URL(urlString)

  if (typeof userId === 'string') {
    url.searchParams.append('refuid', userId)
  }
  return url.toString()
}

export const useSocialSharing = ({ url }: { url: string | undefined }): UseSocialSharingValues | null => {
  const { t } = useTranslate('common')
  const linkCopied = t('linkCopied', 'Link copied.')
  const [link, setLink] = useState<string | null>(null)

  useEffect(() => {
    if (url) {
      setLink(createWebSharingUrl(url))
    }
  }, [url])

  const toast = useToast()

  // link is an async branch url that we need to kick out early if we don't have it.
  if (!link) return null

  const socials = {
    copyLink: {
      name: t('copyLink', 'Copy Link'),
      icon: LinkIcon,
      onClick: () => {
        navigator.clipboard.writeText(link)
        toast?.showToast(linkCopied, { icon: <CopyIcon size={24} color="core-gray-200" /> })
      },
      href: link,
      brandColor: '#000',
    },
    email: {
      name: t('email', 'Email'),
      icon: EnvelopeIcon,
      href: `mailto:?subject=Join%20me%20on%20Angel%20Studios&body=Come%20and%20see%20this%20from%20Angel%20Studios:%0D%0D${link}`,
      brandColor: '#000',
    },
    twitter: {
      name: 'Twitter',
      icon: TwitterXIcon,
      href: `https://twitter.com/share?url=${link}`,
      brandColor: '#1DA1F2',
    },
    facebook: {
      name: 'Facebook',
      icon: FacebookIcon,
      href: `https://www.facebook.com/sharer/sharer.php?u=${link}`,
      brandColor: '#1877F2',
    },
    reddit: {
      name: 'Reddit',
      icon: RedditIcon,
      href: `http://reddit.com/submit?url=${link}`,
      brandColor: '#FF4500',
    },
    telegram: {
      name: 'Telegram',
      icon: TelegramIcon,
      href: `https://telegram.me/share/url?url=${link}`,
      brandColor: '#27A7E7',
    },
  }
  return {
    socials,
    links: Object.values(socials),
  }
}

export const SocialSharing: React.FC<SocialSharingProps> = ({
  color = 'core-gray-700',
  url: sharingUrl,
  vertical = false,
  onShare,
  viewImpact,
  showImpact,
  projectSlug,
}) => {
  const [url, setUrl] = useState<string | undefined>(sharingUrl)
  const [copied, setCopy] = useState<boolean>(false)
  const [copiedHref, setCopiedHref] = useState<string>('')
  const socialSharingLinks = useSocialSharing({ url })
  const { isLoggedIn } = useUser()
  const { t } = useTranslate('common')
  const { loginUrl, trackLoginStarted } = useLoginUrl()
  const toast = useToast()

  useEffect(() => {
    setUrl(encodeURI(`${paths.getBaseURL()}/tickets/${projectSlug}`))
  }, [projectSlug])

  const handleButtonClick = useCallback(() => {
    setCopy(true)
    setTimeout(() => {
      setCopy(false)
    }, 5000)
  }, [])

  const handleHrefButtonClick = useCallback((name: string) => {
    setCopiedHref(name)
    setTimeout(() => {
      setCopiedHref('')
    }, 5000)
  }, [])

  return (
    <div className={classNames(!showImpact && 'pb-6 pt-3')}>
      {!showImpact && (
        <div>
          {socialSharingLinks?.links?.map(({ name, href, onClick }) => {
            if (onClick) {
              return (
                <div key={name} className="w-full max-w-full pb-4">
                  <div className="flex w-full flex-row rounded border border-solid border-core-gray-400 bg-[#F8F7F7] lg:flex-col 2xl:flex-row">
                    <input type="text" defaultValue={href ?? ''} disabled className="w-full p-2" />
                    <Button
                      variant="white"
                      disabled={copied}
                      className={`justify-right items-right m-2 flex gap-2 border border-solid !border-core-gray-950 p-2 text-sm`}
                      onClick={() => {
                        navigator.clipboard.writeText(href ?? '')
                        handleButtonClick()
                        toast?.showToast('copied link', { icon: <CopyIcon size={16} color="core-gray-200" /> })
                      }}
                    >
                      {copied ? (
                        <CheckIcon color={'white'} size={16} />
                      ) : (
                        <LinkIcon color={'core-gray-950'} size={12} />
                      )}
                      {copied ? t('copied', 'Copied') : t('copyV1', 'Copy')}
                    </Button>
                  </div>
                </div>
              )
            }
          })}
        </div>
      )}
      {!showImpact && (
        <div
          className={classNames(
            'w-full flex items-center justify-center flex-col md:flex-row lg:flex-col 2xl:flex-row',
          )}
        >
          <div className="flex w-full max-w-[250px] flex-row justify-between">
            {socialSharingLinks?.links?.map(({ name, href, onClick, icon: Icon }) => {
              if (href && !onClick) {
                return (
                  <a
                    aria-label={name}
                    key={name}
                    className={classNames(
                      'flex flex-row focus-visible:outline-none focus-visible:ring-0 focus-visible:right-offset-0 focus-visible:ring-transparent transition transition-speed-[2s] ease-in-out rounded-full w-10 h-10 justify-center items-center',
                      copiedHref === name
                        ? 'bg-core-gray-950 cursor-not-allowed pointer-events-none'
                        : 'bg-core-gray-200 hover:bg-core-gray-400 cursor-pointer',
                    )}
                    href={href}
                    rel="noreferrer"
                    target="_blank"
                    onClick={() => {
                      onShare?.(name)
                      handleHrefButtonClick(name)
                    }}
                  >
                    {copiedHref === name ? (
                      <CheckIcon color={'white'} size={16} />
                    ) : (
                      <Icon color={'core-gray-950'} size={16} />
                    )}
                    <When condition={vertical}>
                      <ParagraphMD className="my-auto cursor-pointer" color={color}>
                        {copied ? t('copied', 'Copied') : t('copyV1', 'Copy')}
                      </ParagraphMD>
                    </When>
                  </a>
                )
              } else if (onClick) {
                return <></>
              }
            })}
          </div>
          <Button
            variant="gray"
            outline={true}
            disabled={!isLoggedIn}
            className={classNames(
              `flex flex-row flex-nowrap justify-right items-right gap-2 p-2 text-xs ml-0 md:ml-2 lg:ml-0 2xl:ml-2 min-h-[40px] min-w-[120px] w-full md:w-fit lg:w-full 2xl:w-fit mt-4 md:mt-0 lg:mt-4 2xl:mt-0 relative group`,
              showImpact ? 'bg-core-gray-400' : 'bg-[#F8F7F7]',
            )}
            onClick={viewImpact}
          >
            <div className={classNames(!isLoggedIn && 'opacity-40')}>
              <AnalyticsIcon color={'core-gray-500'} size={16} />
            </div>
            {t('viewImpact', 'View Impact')}
            {!isLoggedIn && (
              <div className="absolute bottom-0 mb-9 flex w-fit min-w-[310px] flex-col items-center group-hover:flex">
                <span className="whitespace-no-wrap relative z-10 cursor-auto rounded-lg border border-solid border-core-gray-400 bg-white p-3 text-[10px] font-[375] leading-4 text-core-gray-700">
                  <LinkButton
                    className="m-0 inline border-none p-0 text-black underline"
                    href={loginUrl}
                    variant="plain"
                    onClick={() => trackLoginStarted('bottom-sharing-block')}
                  >
                    {t('loginOrCreateAFreeAccount', 'Login or Create a free account')}
                  </LinkButton>{' '}
                  {t(
                    'toSeeHowManyPeopleYouHaveImpactedBecauseOfYourShare',
                    'to see how many people you impacted because of your share.',
                  )}
                </span>
                <div
                  className={classNames(
                    'w-4 h-6 -mt-4 rotate-45 bg-white bg-opacity-100 border border-solid border-core-gray-400',
                  )}
                ></div>
              </div>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
